import { Bar, Column, Line } from '@ant-design/charts';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { formatCompactCurrency, formatInteger } from '../../../../../services/util/number';

export const TOTAL_METRICS = { VALUE: 'value', COOPERATORS: 'cooperators' };

export const TOTAL_CHART_TYPES = { BAR: 'bar', COLUMN: 'column', LINE: 'line' };

const TOTAL_COLOR = '#13C2C2';
const DEFAULT_CHART_HEIGHT = 360;
// Nas barras horizontais cada competência ganha uma linha própria, então o gráfico cresce com o período
// em vez de espremer 24 barras (e seus rótulos) na largura do card
const BAR_ROW_HEIGHT = 32;

const FIELD_BY_METRIC = {
  [TOTAL_METRICS.VALUE]: 'valorTotal',
  [TOTAL_METRICS.COOPERATORS]: 'quantidadeCpfs',
};

const FORMATTER_BY_METRIC = {
  [TOTAL_METRICS.VALUE]: formatCompactCurrency,
  [TOTAL_METRICS.COOPERATORS]: formatInteger,
};

const CHART_BY_TYPE = {
  [TOTAL_CHART_TYPES.BAR]: Bar,
  [TOTAL_CHART_TYPES.COLUMN]: Column,
  [TOTAL_CHART_TYPES.LINE]: Line,
};

const ChartWrapper = styled.div`
  height: ${({ $height }) => $height}px;
`;

export function resolveChartHeight(chartType, competenceCount) {
  if (chartType !== TOTAL_CHART_TYPES.BAR) return DEFAULT_CHART_HEIGHT;
  return Math.max(DEFAULT_CHART_HEIGHT, competenceCount * BAR_ROW_HEIGHT);
}

export function buildTotalChartConfig({ data, competenceValues, metric, chartType, tooltip }) {
  const metricField = FIELD_BY_METRIC[metric];
  const formatMetric = FORMATTER_BY_METRIC[metric];
  const labelFormatter = (datum) => formatMetric(datum[metricField]);

  const configByChartType = {
    [TOTAL_CHART_TYPES.BAR]: {
      xField: metricField,
      yField: 'competenceLabel',
      barStyle: { radius: [4, 4, 0, 0] },
      label: { position: 'right', formatter: labelFormatter },
      // espaço à direita para o rótulo da maior barra não ser cortado
      appendPadding: [0, 72, 0, 0],
    },
    [TOTAL_CHART_TYPES.COLUMN]: {
      xField: 'competenceLabel',
      yField: metricField,
      columnStyle: { radius: [4, 4, 0, 0] },
      label: { position: 'top', formatter: labelFormatter, layout: [{ type: 'interval-hide-overlap' }] },
      appendPadding: [16, 0, 0, 0],
    },
    [TOTAL_CHART_TYPES.LINE]: {
      xField: 'competenceLabel',
      yField: metricField,
      point: { size: 4 },
      label: { formatter: labelFormatter, layout: [{ type: 'hide-overlap' }] },
      appendPadding: [16, 16, 0, 0],
    },
  };

  return {
    data,
    color: TOTAL_COLOR,
    tooltip,
    meta: {
      competenceLabel: { values: competenceValues },
      [metricField]: { formatter: formatMetric },
    },
    ...configByChartType[chartType],
  };
}

function TotalChart({ data, competenceValues, metric, chartType, tooltip }) {
  const Chart = CHART_BY_TYPE[chartType];
  const config = buildTotalChartConfig({ data, competenceValues, metric, chartType, tooltip });

  return (
    <ChartWrapper $height={resolveChartHeight(chartType, competenceValues.length)}>
      {/* key: o update do G2Plot não troca os campos dos eixos de forma confiável ao mudar a métrica */}
      <Chart
        key={metric}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...config}
      />
    </ChartWrapper>
  );
}

TotalChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      competenceLabel: PropTypes.string.isRequired,
      quantidadeCpfs: PropTypes.number.isRequired,
      valorTotal: PropTypes.number.isRequired,
    })
  ).isRequired,
  competenceValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  metric: PropTypes.oneOf(Object.values(TOTAL_METRICS)).isRequired,
  chartType: PropTypes.oneOf(Object.values(TOTAL_CHART_TYPES)).isRequired,
  tooltip: PropTypes.shape({}).isRequired,
};

export default TotalChart;
